import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Download, HardDrive, Mail, RefreshCw, PlayCircle, ListChecks } from "lucide-react";

export default function Administration() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [standards, setStandards] = useState([]);
  const [backups, setBackups] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email:"", name:"", role:"staff", password:"" });
  const [busy, setBusy] = useState("");

  const loadBackups = useCallback(async () => {
    try { const { data } = await api.get("/admin/backups"); setBackups(data.backups || []); }
    catch (err) { console.warn("Backups fetch failed", err); }
  }, []);

  useEffect(() => {
    const role = user?.role;
    if (role !== "admin" && role !== "auditor" && role !== "park_manager") return;
    api.get("/auth/users").then((r)=>setUsers(r.data)).catch((err)=>console.warn("Users fetch failed", err));
    api.get("/audit").then((r)=>setAudit(r.data)).catch((err)=>console.warn("Audit fetch failed", err));
    api.get("/standards").then((r)=>setStandards(r.data));
    if (role === "admin") loadBackups();
  }, [user?.role, loadBackups]);

  const createUser = async () => {
    try { await api.post("/auth/register", f); toast.success("User created"); setOpen(false); setF({email:"",name:"",role:"staff",password:""}); const r=await api.get("/auth/users"); setUsers(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const runBackup = async () => {
    setBusy("backup");
    try { const { data } = await api.post("/admin/backups/run"); toast.success(`Backup complete (${(data.size/1024).toFixed(1)} KB)`); loadBackups(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    setBusy("");
  };
  const runDigest = async () => {
    setBusy("digest");
    try { const { data } = await api.post("/admin/digest/run"); toast.success(`Digest sent to ${data.sent} recipient(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Digest failed"); }
    setBusy("");
  };
  const consolidateTasks = async () => {
    setBusy("consolidate");
    try {
      const dry = await api.post("/admin/consolidate-tasks?dry_run=true");
      const { renamed, merged } = dry.data;
      if (renamed === 0 && merged === 0) {
        toast.info("Nothing to streamline — titles already unique.");
        setBusy(""); return;
      }
      const proceed = window.confirm(
        `This will rename ${renamed} task title(s) to include the asset location and merge ${merged} exact duplicate(s). ` +
        "Tasks with completion history are never touched. Continue?",
      );
      if (!proceed) { setBusy(""); return; }
      const { data } = await api.post("/admin/consolidate-tasks");
      toast.success(`Streamlined: ${data.renamed} renamed, ${data.merged} merged`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Consolidation failed"); }
    setBusy("");
  };
  const testEmail = async () => {
    setBusy("email");
    try { await api.post("/admin/email/test", {}); toast.success(`Test email sent to ${user.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    setBusy("");
  };
  const exportZip = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export.zip`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">Settings</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Administration</h1>
      </div>

      {user?.role === "admin" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Download className="w-4 h-4 text-slate-700"/><h3 className="font-heading text-base font-semibold">Full data export</h3></div>
            <p className="text-xs text-slate-600 mb-3">Everything (assets, tasks, inspections, docs) as a portable zip.</p>
            <Button size="sm" className="w-full" onClick={exportZip} data-testid="admin-export-btn">Download zip</Button>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><HardDrive className="w-4 h-4 text-slate-700"/><h3 className="font-heading text-base font-semibold">Manual backup</h3></div>
            <p className="text-xs text-slate-600 mb-3">Nightly at 15:00 UTC. Run one now if you need it.</p>
            <Button size="sm" className="w-full" variant="outline" onClick={runBackup} disabled={busy==="backup"} data-testid="admin-backup-btn">{busy==="backup"?"Running…":"Run backup now"}</Button>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-slate-700"/><h3 className="font-heading text-base font-semibold">Test email</h3></div>
            <p className="text-xs text-slate-600 mb-3">Send a Resend test to {user.email} to verify delivery.</p>
            <Button size="sm" className="w-full" variant="outline" onClick={testEmail} disabled={busy==="email"} data-testid="admin-test-email-btn">{busy==="email"?"Sending…":"Send test"}</Button>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><PlayCircle className="w-4 h-4 text-slate-700"/><h3 className="font-heading text-base font-semibold">Run digest now</h3></div>
            <p className="text-xs text-slate-600 mb-3">Regenerate notifications &amp; email today&apos;s summary to admins.</p>
            <Button size="sm" className="w-full" variant="outline" onClick={runDigest} disabled={busy==="digest"} data-testid="admin-digest-btn">{busy==="digest"?"Sending…":"Run digest"}</Button>
          </CardContent></Card>
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><ListChecks className="w-4 h-4 text-slate-700"/><h3 className="font-heading text-base font-semibold">Streamline tasks</h3></div>
            <p className="text-xs text-slate-600 mb-3">Prefix titles with asset location so identical-looking tasks are distinguishable.</p>
            <Button size="sm" className="w-full" variant="outline" onClick={consolidateTasks} disabled={busy==="consolidate"} data-testid="admin-consolidate-btn">{busy==="consolidate"?"Running…":"Run consolidation"}</Button>
          </CardContent></Card>
        </div>
      )}

      {user?.role === "admin" && (
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><HardDrive className="w-4 h-4"/>Backups</h3>
            <Button size="sm" variant="ghost" onClick={loadBackups} data-testid="refresh-backups"><RefreshCw className="w-4 h-4 mr-1"/>Refresh</Button>
          </div>
          {backups.length === 0 ? <div className="text-sm text-slate-500 py-4">No backups yet. Nightly job hasn&apos;t run — click &ldquo;Run backup now&rdquo; above to create one.</div> : (
            <Table className="dense-table">
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Size</TableHead><TableHead>Path</TableHead></TableRow></TableHeader>
              <TableBody>{backups.map((b)=>(<TableRow key={b.date}><TableCell className="font-mono text-xs">{b.date}</TableCell><TableCell className="text-xs">{(b.size/1024).toFixed(1)} KB</TableCell><TableCell className="text-xs font-mono text-slate-500">{b.path}</TableCell></TableRow>))}</TableBody>
            </Table>
          )}
        </CardContent></Card>
      )}

      {user?.role === "admin" && (
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-semibold">Users</h3>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1"/>New user</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name:e.target.value})}/></div>
                  <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} data-testid="new-user-email"/></div>
                  <div><Label>Role</Label>
                    <Select value={f.role} onValueChange={(v)=>setF({...f, role:v})}>
                      <SelectTrigger data-testid="new-user-role"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["admin","park_manager","staff","contractor","auditor"].map((r)=><SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Temporary password</Label><Input type="text" value={f.password} onChange={(e)=>setF({...f, password:e.target.value})} data-testid="new-user-pw"/></div>
                </div>
                <DialogFooter><Button onClick={createUser} disabled={!f.email||!f.name||!f.password} data-testid="new-user-save">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Table className="dense-table">
            <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u)=>(<TableRow key={u.id}><TableCell className="text-sm">{u.email}</TableCell><TableCell className="text-sm">{u.name}</TableCell><TableCell><Badge variant="outline">{u.role}</Badge></TableCell><TableCell className="text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell></TableRow>))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      <Card className="border-slate-200"><CardContent className="p-4">
        <h3 className="font-heading text-lg font-semibold mb-3">Standards &amp; sources (imported)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {standards.map((s)=>(
            <div key={s.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">{s.area}</div>
              <div className="font-medium text-slate-900">{s.standard}</div>
              {s.application && <div className="text-xs text-slate-600 mt-1">{s.application}</div>}
              {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">{s.source_url}</a>}
            </div>
          ))}
        </div>
      </CardContent></Card>

      <Card className="border-slate-200"><CardContent className="p-4">
        <h3 className="font-heading text-lg font-semibold mb-3">Recent audit trail</h3>
        <Table className="dense-table">
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>ID</TableHead></TableRow></TableHeader>
          <TableBody>
            {audit.slice(0, 40).map((a)=>(<TableRow key={a.id}>
              <TableCell className="text-xs font-mono">{new Date(a.timestamp).toLocaleString()}</TableCell>
              <TableCell className="text-xs">{a.user_email||"—"}</TableCell>
              <TableCell><Badge variant="outline">{a.action}</Badge></TableCell>
              <TableCell className="text-xs">{a.entity_type}</TableCell>
              <TableCell className="text-xs font-mono">{a.entity_id?.slice(0,8)}</TableCell>
            </TableRow>))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
